import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import {
  Playbook,
  PlaybookCopilotDestination,
} from '@trustero/trustero-api-web/lib/playbook/playbook_pb'
import { Chat } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import log from 'loglevel'
import { toast } from 'react-toastify'
import { FlexColumn } from 'src/components/Reusable/Flex'
import palette from 'src/designSystem/variables/palette'
import {
  LargeText,
  SmallText,
  TinyText,
} from 'src/global.styles/typography.styles'
import { formatISODate } from 'src/Utils/formatDate'
import { useAgentChat, useHasCopilotShowPage } from 'src/context/Gatekeeper'
import { ChatPromptInput } from 'src/pages/TrusteroIntelligence/trusteroIntelligence.components'
import {
  useIsPlaybookVisible,
  usePlaybooks,
  useImportQuestionnaireDestination,
  usePreparePlaybookLaunch,
} from 'src/pages/TrusteroIntelligence/playbooks/playbooks.hooks'
import { useSendChatMessage } from 'src/pages/TrusteroIntelligence/trusteroIntelligence.hooks'
import { useSetPendingPromptForChat } from 'src/pages/TrusteroIntelligence/trusteroIntelligence.context'

import { useAppCopilotContext } from './AppCopilot.context'

const LibraryContainer = styled(FlexColumn)`
  height: 100%;
  overflow: hidden;
  align-items: stretch;
  justify-content: flex-start;
  padding-left: 8px;
  justify-content: space-between;
`

const WelcomeText = styled.p`
  color: ${palette.neutral[900]};
  font-family: Poppins;
  font-size: 14px;
  font-weight: 500;
  line-height: 30px;
  padding: 16px 16px 12px;
  margin: 0;
`

const PlaybookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 200px);
  justify-content: start;
  align-content: start;
  gap: 16px;
  padding: 12px 16px;
  overflow-y: auto;
  flex: 1;
`

const CopilotPlaybookCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 200px;
  height: 200px;
  border-radius: 20px;
  background: #f0f4fa;
  padding: 20px;
  gap: 12px;
  cursor: pointer;
  &:hover {
    box-shadow: 0 2px 20px 0 #aab0bb;
  }
`

const CardTitle = styled(LargeText)`
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  color: ${palette.neutral[900]};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const CardDescription = styled(SmallText)`
  letter-spacing: 0.5px;
  color: ${palette.neutral[800]};
  overflow-y: auto;
  flex: 1;
  width: 100%;
`

const CardLastUpdated = styled(TinyText)`
  line-height: 150%;
  color: ${palette.neutral.black};
  width: 100%;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #b5cce3;
`

const BottomSection = styled.div`
  margin-top: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

type CopilotPlaybookCardProps = {
  playbook: Playbook
  onRun: () => void
}

const CopilotPlaybookCard = ({
  playbook,
  onRun,
}: CopilotPlaybookCardProps): JSX.Element => (
  <CopilotPlaybookCardContainer onClick={onRun}>
    <CardTitle title={playbook.getName()}>{playbook.getName()}</CardTitle>
    <CardDescription title={playbook.getDescription()}>
      {playbook.getDescription()}
    </CardDescription>
    {playbook.getUpdatedAt() > 0 && (
      <CardLastUpdated>
        Last updated: {formatISODate(playbook.getUpdatedAt())}
      </CardLastUpdated>
    )}
  </CopilotPlaybookCardContainer>
)

type CopilotLibraryViewProps = {
  promptTopContent?: React.ReactNode
  createCopilotChat: (
    title: string,
    leadPrompt?: string,
    skipViewSwitch?: boolean,
  ) => Promise<{ chat: Chat }>
  copilotDestination?: PlaybookCopilotDestination
}

export const CopilotLibraryView = ({
  promptTopContent,
  createCopilotChat,
  copilotDestination,
}: CopilotLibraryViewProps): JSX.Element => {
  const {
    show,
    setView,
    setChatId,
    writeLastVisitedChat,
    referringContext,
    pendingPlaybookLaunch,
    setPendingPlaybookLaunch,
    playbookFilter,
  } = useAppCopilotContext()
  const isAgentChat = useAgentChat()
  const hasCopilotShowPage = useHasCopilotShowPage()

  const isPlaybookVisible = useIsPlaybookVisible()
  const [isLaunching, setIsLaunching] = useState(false)
  const [busy, setBusy] = useState(false)
  const [showPromptInput, setShowPromptInput] = useState(false)
  const consumedPendingLaunchRef = useRef<typeof pendingPlaybookLaunch>()

  const sendChatMessage = useSendChatMessage()
  const setPendingPromptForChat = useSetPendingPromptForChat()

  const { data: playbooksData, isLoading: playbooksLoading } = usePlaybooks({
    includeGlobalPlaybooks: true,
  })

  const playbooksForLaunch = useMemo(
    () => playbooksData?.getPlaybooksList() || [],
    [playbooksData],
  )

  const preparePlaybookLaunch = usePreparePlaybookLaunch()
  const importQuestionnaireDestination = useImportQuestionnaireDestination()

  const allPlaybooks = useMemo(
    () =>
      (playbooksData?.getPlaybooksList() || []).filter((playbook) => {
        // Hide Test Procedure Refinement from non-show-page copilots
        if (
          !hasCopilotShowPage &&
          playbook.getIsGlobal() &&
          playbook.getName() === 'Test Procedure Refinement'
        ) {
          return false
        }

        // Feature-flag gate
        if (!isPlaybookVisible(playbook)) return false

        // The page's own rule, when it has one
        if (playbookFilter && !playbookFilter(playbook)) return false

        // Destination match
        if (
          copilotDestination !== undefined &&
          playbook.getCopilotDestinationsList().includes(copilotDestination)
        ) {
          return true
        }

        // Existing parameter-type match
        return playbook
          .getPromptsList()
          .some((prompt) =>
            prompt
              .getParametersList()
              .some(
                (param) =>
                  param.getParameterType() === referringContext.objectType,
              ),
          )
      }),
    [
      playbooksData,
      referringContext.objectType,
      hasCopilotShowPage,
      copilotDestination,
      isPlaybookVisible,
      playbookFilter,
    ],
  )

  const onLibrarySend = useCallback(
    async (prompt: string, _messageType?: unknown, fileUploads?: File[]) => {
      if (!prompt.trim()) return
      setBusy(true)
      try {
        const { chat } = await createCopilotChat('', undefined, true)
        let sendError: unknown
        await sendChatMessage({
          chat,
          prompt,
          fileUploads,
          onSuccess: async (questionId, taskId) => {
            setPendingPromptForChat(chat.getId(), {
              taskId,
              prompt,
              questionId,
            })
          },
          onError: (err) => {
            sendError = err
          },
        })
        if (sendError) throw sendError
        setChatId(chat.getId())
        writeLastVisitedChat(chat.getId())
        setView('conversation')
      } catch (error) {
        log.error('CopilotLibraryView: error creating chat from prompt', error)
        toast.error('Failed to start chat')
      } finally {
        setBusy(false)
      }
    },
    [
      createCopilotChat,
      sendChatMessage,
      setPendingPromptForChat,
      setChatId,
      writeLastVisitedChat,
      setView,
    ],
  )

  const onRunPlaybook = useCallback(
    async (playbook: Playbook) => {
      try {
        setIsLaunching(true)
        const leadPrompt = playbook.getLeadPrompt() || playbook.getDescription()
        const { chat } = await createCopilotChat(
          playbook.getName(),
          leadPrompt || undefined,
          true,
        )
        const { playbookInputs, initiatePrompt } = preparePlaybookLaunch(
          playbook,
          // The page says what an import is for: on Customer Questionnaires it
          // is one somebody sent us, on Vendor Management a template to send
          // out. Settled here so the playbook does not stop to ask.
          importQuestionnaireDestination(playbook, referringContext.objectType),
        )
        let sendError: unknown
        await sendChatMessage({
          chat,
          prompt: initiatePrompt || '',
          playbookInputs,
          onSuccess: async (questionId, taskId) => {
            setPendingPromptForChat(chat.getId(), {
              taskId,
              prompt: initiatePrompt || '',
              questionId,
            })
          },
          onError: (err) => {
            sendError = err
          },
        })
        if (sendError) throw sendError
        setChatId(chat.getId())
        writeLastVisitedChat(chat.getId())
        setView('conversation')
      } catch (error) {
        log.error('CopilotLibraryView: error launching playbook', error)
        toast.error('Failed to launch playbook')
      } finally {
        setIsLaunching(false)
      }
    },
    [
      createCopilotChat,
      preparePlaybookLaunch,
      importQuestionnaireDestination,
      referringContext.objectType,
      sendChatMessage,
      setPendingPromptForChat,
      setChatId,
      writeLastVisitedChat,
      setView,
    ],
  )

  // Auto-launch a playbook when pendingPlaybookLaunch is set from outside
  // (e.g., "Refine with Trustero Intelligence" button on controls page)
  useEffect(() => {
    if (!pendingPlaybookLaunch) {
      consumedPendingLaunchRef.current = undefined
      return
    }
    if (
      consumedPendingLaunchRef.current === pendingPlaybookLaunch ||
      !playbooksForLaunch.length ||
      isLaunching
    ) {
      return
    }
    consumedPendingLaunchRef.current = pendingPlaybookLaunch
    const playbookId = pendingPlaybookLaunch.getPlaybookId()
    const playbook = playbooksForLaunch.find((p) => p.getId() === playbookId)
    if (!playbook) {
      consumedPendingLaunchRef.current = undefined
      setPendingPlaybookLaunch(undefined)
      return
    }
    setPendingPlaybookLaunch(undefined)
    const launchPending = async () => {
      try {
        setIsLaunching(true)
        const leadPrompt = playbook.getLeadPrompt() || playbook.getDescription()
        const { chat } = await createCopilotChat(
          playbook.getName(),
          leadPrompt || undefined,
          true,
        )
        let sendError: unknown
        await sendChatMessage({
          chat,
          prompt: '',
          playbookInputs: pendingPlaybookLaunch,
          onSuccess: async (questionId, taskId) => {
            setPendingPromptForChat(chat.getId(), {
              taskId,
              prompt: '',
              questionId,
            })
          },
          onError: (err) => {
            sendError = err
          },
        })
        if (sendError) throw sendError
        setChatId(chat.getId())
        writeLastVisitedChat(chat.getId())
        setView('conversation')
      } catch (error) {
        log.error('Error auto-launching playbook:', error)
        toast.error('Failed to launch playbook')
      } finally {
        setIsLaunching(false)
      }
    }
    void launchPending()
  }, [
    pendingPlaybookLaunch,
    setPendingPlaybookLaunch,
    playbooksForLaunch,
    isLaunching,
    createCopilotChat,
    sendChatMessage,
    setPendingPromptForChat,
    setChatId,
    writeLastVisitedChat,
    setView,
  ])

  useEffect(() => {
    if (!show) {
      setShowPromptInput(false)
      return
    }
    // Delay mounting the input until after the panel open transition (250ms)
    // so DynamicTextInput reads scrollHeight from a correctly laid-out container.
    const timeout = setTimeout(() => setShowPromptInput(true), 300)
    return () => clearTimeout(timeout)
  }, [show])

  if (playbooksLoading) {
    return (
      <LibraryContainer>
        <WelcomeText>
          Welcome to Trustero Intelligence. Leverage our automated playbooks or
          use AI prompts to unlock deep evidence insights.
        </WelcomeText>
      </LibraryContainer>
    )
  }

  return (
    <LibraryContainer>
      <WelcomeText>
        Welcome to Trustero Intelligence. Leverage our automated playbooks or
        use AI prompts to unlock deep evidence insights.
      </WelcomeText>
      <PlaybookGrid>
        {allPlaybooks.map((playbook) => (
          <CopilotPlaybookCard
            key={playbook.getId()}
            playbook={playbook}
            onRun={() => void onRunPlaybook(playbook)}
          />
        ))}
      </PlaybookGrid>
      <BottomSection>
        {promptTopContent}
        {showPromptInput && (
          <div>
            <ChatPromptInput
              onSend={onLibrarySend}
              busy={busy || isLaunching}
              isAgentChat={isAgentChat}
              isCopilotMode={true}
              isNewChat={true}
            />
          </div>
        )}
      </BottomSection>
    </LibraryContainer>
  )
}
